import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, LogOut, Edit, QrCode, Download, Printer } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import PrintableQRCard from "@/components/admin/PrintableQRCard";
import { publicDateLabel } from "@/lib/tributeDates";

const SITE_URL = "https://legacylensai.com";

type Tribute = {
  id: string;
  slug: string;
  full_name: string;
  date_of_birth: string | null;
  date_of_death: string | null;
  is_published: boolean;
  created_at: string;
};

const AdminTributes = () => {
  const [tributes, setTributes] = useState<Tribute[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTributes();
  }, []);

  const fetchTributes = async () => {
    const { data } = await supabase
      .from("tributes")
      .select("id, slug, full_name, date_of_birth, date_of_death, is_published, created_at")
      .order("created_at", { ascending: false });
    setTributes(data || []);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-8" />
            <span className="font-display text-lg font-semibold text-foreground">Admin</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl font-bold text-foreground">Tributes</h1>
          <Button asChild>
            <Link to="/admin/tributes/new"><Plus className="w-4 h-4 mr-2" /> New Tribute</Link>
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : tributes.length === 0 ? (
          <p className="text-muted-foreground">No tributes yet. Create your first one.</p>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-28"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tributes.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.full_name}</TableCell>
                    <TableCell className="text-muted-foreground">/tribute/{t.slug}</TableCell>
                    <TableCell>
                      <Badge variant={t.is_published ? "default" : "secondary"}>
                        {t.is_published ? "Published" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" title="QR Code">
                            <QrCode className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="text-center">QR Code for {t.full_name}</DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col items-center gap-4 py-4">
                            <QRCodeCanvas
                              id={`qr-${t.slug}`}
                              value={`${SITE_URL}/tribute/${t.slug}`}
                              size={200}
                              level="H"
                              includeMargin
                            />
                            <p className="text-sm text-muted-foreground text-center break-all">
                              {SITE_URL}/tribute/{t.slug}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const canvas = document.getElementById(`qr-${t.slug}`) as HTMLCanvasElement;
                                if (!canvas) return;
                                const url = canvas.toDataURL("image/png");
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `${t.slug}-qr.png`;
                                a.click();
                              }}
                            >
                              <Download className="w-4 h-4 mr-2" /> Download PNG
                            </Button>
                          </div>
                          <div className="border-t border-border pt-4">
                            <p className="text-xs text-muted-foreground text-center mb-3">Printable Card Preview</p>
                            <PrintableQRCard
                              slug={t.slug}
                              fullName={t.full_name}
                              dateLabel={publicDateLabel(t.date_of_birth, t.date_of_death)}
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/admin/tributes/${t.id}/edit`}><Edit className="w-4 h-4" /></Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTributes;
